import { useState } from "react";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useDismiss,
  useRole,
  useClick,
  useHover,
  useInteractions,
  FloatingFocusManager,
  useId,
  safePolygon,
} from "@floating-ui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUser } from "@fortawesome/pro-regular-svg-icons";
import Typography from "../Typography/Typography.jsx";
import styled from "styled-components";

export const MenuContainer = styled.div`
  min-width: 160px;
  width: max-content;
  border: 1px solid #dddbda;
  border-radius: 4px;
  background-color: #fff;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2);

  ${(props) =>
    props?.navProfilePopover &&
    `
    border-radius: 6px;
    padding: 10px;
    width: 205px;
  `}

  ${(props) =>
    props?.navProfilePopover &&
    !props?.$isNavOpen &&
    `
    padding: 8px;
  `}
`;

export const DarkContainer = styled.div`
  min-width: 120px;
  max-width: 280px;
  background-color: #300866;
  border-radius: 4px;
  padding: 10px 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  color: #ffffff;

  &::before {
    content: "";
    position: absolute;
    width: 8px;
    height: 8px;
    background-color: #300866;
    transform: rotate(45deg);

    ${({ $placement }) => {
      switch ($placement) {
        case "bottom":
          return "top: -4px; left: 50%; margin-left: -4px;";
        case "top":
          return "bottom: -4px; left: 50%; margin-left: -4px;";
        case "left":
          return "right: -4px; top: 50%; margin-top: -4px;";
        case "right":
          return "left: -4px; top: 50%; margin-top: -4px;";
        default:
          return "bottom: -4px; left: 50%; margin-left: -4px;";
      }
    }}
  }
`;

export const MenuItem = styled.div`
  font-family: "Inter";
  padding: 12px 18px;
  font-size: 14px;
  color: #080707;
  background-color: #ffffff;
  cursor: pointer;

  :not(:last-child) {
    border-bottom: 1px solid #dddbda;
  }

  :hover {
    background-color: #f5f2fc;
  }

  ${(props) =>
    props?.navProfilePopover &&
    `
    font-size: 12px;

    :hover {
        border-radius: 6px;
      }

      :not(:last-child) {
        border-bottom: none;
      }
  `}
`;

export const ProfileMenu = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
  background-color: #ffffff;
  border-bottom: 1px solid #979797;
  margin-bottom: 10px;
  padding: 8px;
  padding-bottom: 12px;

  ${(props) =>
    props.$isNavOpen &&
    `
    justify-content: flex-start;
    gap: 10px;
    align-items: flex-start;

    svg {
        margin-top: 1px;
    }
  `}
`;

/**
 * Shared floating-ui foundation used by both Popover and Tooltip.
 * Handles useFloating, interactions, and rendering logic.
 */
export const FloatingContent = ({
  isOpen,
  setIsOpen,
  content,
  interaction = "click",
  placement = "top",
  strategy = "absolute",
  offset: offsetValue = 0,
  container: ContainerComponent = MenuContainer,
  containerProps = {},
  TriggerComponent,
  triggerElement,
  ...customProps
}) => {
  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    strategy,
    placement,
    middleware: [
      offset(offsetValue),
      flip({ fallbackAxisSideDirection: "end" }),
      shift(),
    ],
    whileElementsMounted: autoUpdate,
  });

  const isHover = interaction === "hover";
  const click = useClick(context, { enabled: !isHover });
  const hover = useHover(context, {
    move: false,
    handleClose: safePolygon(),
    enabled: isHover,
  });
  const dismiss = useDismiss(context);
  const role = useRole(context);

  const interactionsList = [dismiss, role, click, hover];

  const { getReferenceProps, getFloatingProps } =
    useInteractions(interactionsList);

  const headingId = useId();

  const triggerElementWithRef = triggerElement ? (
    <span ref={refs.setReference} {...getReferenceProps()}>
      {triggerElement}
    </span>
  ) : null;

  const bridgeStyle =
    offsetValue > 0
      ? placement.startsWith("bottom")
        ? {
            position: "absolute",
            top: -offsetValue,
            left: 0,
            right: 0,
            height: offsetValue,
          }
        : placement.startsWith("top")
        ? {
            position: "absolute",
            bottom: -offsetValue,
            left: 0,
            right: 0,
            height: offsetValue,
          }
        : placement.startsWith("right")
        ? {
            position: "absolute",
            left: -offsetValue,
            top: 0,
            bottom: 0,
            width: offsetValue,
          }
        : placement.startsWith("left")
        ? {
            position: "absolute",
            right: -offsetValue,
            top: 0,
            bottom: 0,
            width: offsetValue,
          }
        : null
      : null;

  return (
    <>
      {TriggerComponent && (
        <TriggerComponent
          setRef={refs.setReference}
          {...customProps}
          {...getReferenceProps()}
        />
      )}
      {triggerElementWithRef}

      {isOpen && (
        <FloatingFocusManager context={context} modal={false}>
          <div
            className="Popover"
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              zIndex: 10000000,
              outline: "none",
            }}
            aria-labelledby={headingId}
            {...getFloatingProps()}
          >
            {bridgeStyle && <div style={bridgeStyle} />}
            <ContainerComponent {...containerProps}>
              {content}
            </ContainerComponent>
          </div>
        </FloatingFocusManager>
      )}
    </>
  );
};

const Popover = ({
  TriggerComponent,
  actions,
  navProfilePopover = false,
  isNavOpen = false,
  placement = "top",
  strategy = "absolute",
  offset: offsetValue = 0,
  ...customProps
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const content = (
    <>
      {navProfilePopover && !isNavOpen && (
        <ProfileMenu
          id="profileMenu"
          $isNavOpen={true}
          data-test="profileMenuButton"
        >
          <FontAwesomeIcon
            icon={faUser}
            style={{
              minWidth: "18px",
              width: "18px",
              padding: "7px 6px",
              color: "#fff",
              backgroundColor: "#00C1F3",
              borderRadius: "3px",
            }}
          />

          {isOpen && (
            <div
              style={{
                height: "36px",
                width: "100%",
                overflow: "hidden",
              }}
            >
              {/* TODO This should render a prop component, not tied to custom props */}
              <Typography variant="h6" color="#080707" noMargin>
                {customProps.name}
              </Typography>
              <Typography variant="sub-text" color="#080707" noMargin>
                {customProps.role}
              </Typography>
            </div>
          )}
        </ProfileMenu>
      )}
      {actions.map((action) => {
        return (
          <MenuItem
            key={action.name}
            data-test={action.dataTest}
            navProfilePopover={navProfilePopover}
            onClickCapture={(event) => action.onClick(event, setIsOpen)}
          >
            {action.name}
          </MenuItem>
        );
      })}
    </>
  );

  return (
    <FloatingContent
      isOpen={isOpen}
      setIsOpen={setIsOpen}
      interaction="click"
      placement={placement}
      strategy={strategy}
      offset={offsetValue}
      TriggerComponent={TriggerComponent}
      isNavOpen={isNavOpen}
      container={MenuContainer}
      containerProps={{ navProfilePopover, $isNavOpen: isNavOpen }}
      content={content}
      {...customProps}
    />
  );
};

export default Popover;
